<script setup lang="ts">
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import KInnerSection from "@/components/generic/KInnerSection.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { useStore } from "@/store";
    import { ANNAT_VAL } from "@/tabs/Receptforskrivning/helpers.js";
    import { computed, inject, nextTick, onMounted, ref, toRefs } from "vue";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            hasSelectedLakemedel: boolean;
            receptforskrivningId?: string;
            fornya: boolean;
        }>(),
        {
            fornya: false,
        }
    );

    const { receptforskrivningId, fornya } = toRefs(props);

    const getRequired = computed(() => {
        return fornya.value ? "" : "required";
    });

    const receptforskrivning = computed(() => {
        return store.getters["receptforskrivning/receptforskrivningById"]({
            id: receptforskrivningId?.value,
        });
    });

    const administreringsmetod = ref(receptforskrivning?.value?.administreringsmetod);
    const administreringsvag = ref(receptforskrivning?.value?.administreringsvag);
    const administreringsstalle = ref(receptforskrivning?.value?.administreringsstalle);
    const precisering_administreringsstalle = ref(receptforskrivning?.value?.precisering_administreringsstalle);
    const medicinteknisk_produkt = ref(receptforskrivning?.value?.medicinteknisk_produkt);
    const doseringInnerSection = ref();

    const formHelpers = inject<{
        getFieldValue: (props: { name: string }) => unknown;
    } | null>(FORM_HELPERS, null);

    defineExpose({
        formListeners: [
            {
                fieldNames: ["administreringsmetod"],
                action: ({ values }) => {
                    administreringsmetod.value = values.administreringsmetod;
                },
            },
            {
                fieldNames: ["administreringsvag"],
                action: ({ values }) => {
                    administreringsvag.value = values.administreringsvag;
                },
            },
            {
                fieldNames: ["administreringsstalle"],
                action: ({ values }) => {
                    administreringsstalle.value = values.administreringsstalle;
                },
            },
            {
                fieldNames: ["precisering_administreringsstalle"],
                action: ({ values }) => {
                    precisering_administreringsstalle.value = values.precisering_administreringsstalle;
                },
            },
            {
                fieldNames: ["medicinteknisk_produkt"],
                action: ({ values }) => {
                    medicinteknisk_produkt.value = values.medicinteknisk_produkt;
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });

    /**
     * Expanderar inner sections som har data satt. Gäller vid inläsning
     */
    const toggleInnerSectionsWithValue = async () => {
        await nextTick();

        const extraData = formHelpers?.getFieldValue?.({ name: "extraData" }) as
            | {
                  sista_doseringsdag?: string;
              }
            | undefined;

        if (extraData?.sista_doseringsdag) {
            doseringInnerSection?.value?.toggle?.();
        }
    };

    onMounted(async () => {
        await toggleInnerSectionsWithValue();
    });
</script>

<template>
    <KSection
        :expanded="hasSelectedLakemedel"
        name="dosering"
        heading="Dosering"
        :collapsible="hasSelectedLakemedel"
        :number="4"
    >
        <div class="form-content readable-disabled">
            <KText
                name="dosering"
                class="span-all"
                label="Dosering"
                suffixText="Visas i doseringsanvisning"
                type="textarea"
                :validation="getRequired"
                :disabled="fornya"
            />
            <KCheckbox name="vid_behov" label="Vid behov" valueType="boolean" :disabled="fornya" />
            <KCheckbox
                name="obs_overstiger_maxdosering"
                label="Obs. överstiger maxdosering"
                valueType="boolean"
                :disabled="fornya"
            />
            <KGroup
                name="administrering"
                label="Administrering"
                class="span-all"
                subText="Välj ett eller flera administreringsval, minst ett obligatoriskt"
                :validation="getRequired"
            >
                <KCheckbox
                    name="visa_administrering"
                    validationName="Administrering"
                    freeFlow
                    direction="horizontal"
                    class="span-all"
                    :validation="getRequired"
                    :disabled="fornya"
                    :options="[
                        { label: 'Metod', value: 'metod' },
                        { label: 'Väg', value: 'vag' },
                        { label: 'Ställe', value: 'stalle' },
                        { label: 'Medicinteknisk produkt', value: 'medicinteknisk_produkt' },
                    ]"
                />
                <KAutocomplete name="administreringsmetod" :options="[]" label="Administreringsmetod" disabled />
                <KText
                    v-if="administreringsmetod === ANNAT_VAL"
                    name="administreringsmetod_annan"
                    label="Annan metod"
                    :validation="getRequired"
                    :disabled="fornya"
                />
                <KAutocomplete name="administreringsvag" :options="[]" label="Administreringsväg" disabled />
                <KText
                    v-if="administreringsvag === ANNAT_VAL"
                    name="administreringsvag_annan"
                    label="Annan väg"
                    :validation="getRequired"
                    :disabled="fornya"
                />
                <KAutocomplete name="administreringsstalle" :options="[]" label="Administreringsställe" disabled />
                <KText
                    v-if="administreringsstalle === ANNAT_VAL"
                    name="administreringsstalle_annan"
                    label="Annat ställe"
                    :validation="getRequired"
                    :disabled="fornya"
                />
                <KAutocomplete
                    name="precisering_administreringsstalle"
                    :options="[]"
                    label="Precisering administreringsställe"
                    disabled
                />
                <KText
                    v-if="precisering_administreringsstalle === ANNAT_VAL"
                    name="precisering_administreringsstalle_annan"
                    label="Annan precisering"
                    :validation="getRequired"
                    :disabled="fornya"
                />
                <KAutocomplete name="medicinteknisk_produkt" :options="[]" label="Medicinteknisk produkt" disabled />
                <KText
                    v-if="medicinteknisk_produkt === ANNAT_VAL"
                    name="medicinteknisk_produkt_annan"
                    label="Annan medicinteknisk produkt"
                    :validation="getRequired"
                    :disabled="fornya"
                />
                <KCheckbox
                    name="administrering_all"
                    label="Visa alla administreringsalternativ"
                    :disabled="fornya"
                    valign="center"
                />
            </KGroup>
            <KText
                name="annan_instruktion"
                label="Annan instruktion"
                type="textarea"
                class="span-two"
                suffixText="Visas i doseringsanvisning"
                :disabled="fornya"
            />
            <KInnerSection
                ref="doseringInnerSection"
                name="dosering-inner-section"
                hiddenContent="Doseringsdatum"
                visibleContent="Doseringsdatum"
            >
                <div class="form-content">
                    <KDateTimepicker name="forsta_doseringsdag" label="Första doseringsdag" :minDate="new Date()" />
                    <KDateTimepicker name="sista_doseringsdag" label="Sista doseringsdag" />
                </div>
            </KInnerSection>
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    .dosering {
    }
</style>
